import { PayloadAction } from '@reduxjs/toolkit';
import {
  PreAcceptCallPayload,
  AcceptCallPayload,
  CallActionPayload,
  InitiateCallPayload,
  FetchCallsPayload,
  FetchCallsResponse,
} from './types';

import { all, call, put, fork, takeLatest } from 'redux-saga/effects';

import { callsActions as actions } from '.';

import api from 'app/api';
import { receiveError } from 'utils/showSuccessErrorModal';

function* initiateCall(action: PayloadAction<InitiateCallPayload>) {
  const { clientWaNumber, session, bizOpaqueCallbackData, refId } =
    action.payload;
  try {
    yield call(api.post, '/calls/initiate', {
      clientWaNumber,
      mode: 'direct',
      session,
      bizOpaqueCallbackData,
      refId,
    });
  } catch (error) {
    receiveError(error);
    yield put(actions.clearCurrentCall(clientWaNumber));
  }
}

function* preAcceptCall(action: PayloadAction<PreAcceptCallPayload>) {
  try {
    const { wacid, session, bizOpaqueCallbackData } = action.payload;
    yield call(api.post, '/calls/pre-accept', {
      wacid,
      session,
      bizOpaqueCallbackData,
    });
  } catch (error) {
    console.error('Error pre-accepting call:', error);
  }
}

function* acceptCall(action: PayloadAction<AcceptCallPayload>) {
  try {
    const { wacid, session, bizOpaqueCallbackData } = action.payload;
    yield call(api.post, '/calls/accept', {
      wacid,
      session,
      bizOpaqueCallbackData,
    });
  } catch (error) {
    console.error('Error accepting call:', error);
  }
}

function* rejectCall(action: PayloadAction<CallActionPayload>) {
  try {
    const { wacid, callType, roomName } = action.payload;
    if (roomName) {
      yield call(api.post, '/calls/reject', { callType, roomName });
    } else {
      yield call(api.post, '/calls/reject', { wacid });
    }
  } catch (error) {
    console.error('Error rejecting call:', error);
  }
}

function* terminateCall(action: PayloadAction<CallActionPayload>) {
  try {
    const { wacid, callType, roomName } = action.payload;
    if (roomName) {
      yield call(api.post, '/calls/terminate', { callType, roomName });
    } else {
      yield call(api.post, '/calls/terminate', { wacid });
    }
  } catch (error) {
    console.error('Error terminating call:', error);
  }
}

export function* watchInitiateCall() {
  yield takeLatest(actions.initiateCallStart.type, initiateCall);
}

export function* watchPreAcceptCall() {
  yield takeLatest(actions.preAcceptCallStart.type, preAcceptCall);
}

export function* watchAcceptCall() {
  yield takeLatest(actions.acceptCallStart.type, acceptCall);
}

export function* watchRejectCall() {
  yield takeLatest(actions.rejectCallStart.type, rejectCall);
}

function* fetchCalls(action: PayloadAction<FetchCallsPayload>) {
  try {
    const { page = 1 } = action.payload;

    // Build query params - only include page, no limit to fetch all grouped records
    const params = new URLSearchParams();
    params.append('page', page.toString());

    const response = yield call(api.get, `/calls?${params.toString()}`);

    const data: FetchCallsResponse = response.data.data;
    yield put(actions.fetchCallsSuccess(data));
  } catch (error) {
    yield put(
      actions.fetchCallsFailure('Failed to fetch grouped call history'),
    );
    receiveError(error);
  }
}

function* watchTerminateCall() {
  yield takeLatest(actions.terminateCallStart.type, terminateCall);
}

function* watchFetchCalls() {
  yield takeLatest(actions.fetchCallsStart.type, fetchCalls);
}

export function* callsSaga() {
  const sagas = [
    watchInitiateCall,
    watchPreAcceptCall,
    watchAcceptCall,
    watchRejectCall,
    watchTerminateCall,
    watchFetchCalls,
  ];

  yield all(
    sagas.map(saga =>
      fork(function* () {
        while (true) {
          try {
            yield call(saga);
            break;
          } catch (e) {
            console.error('Saga error in callsSaga:', e);
          }
        }
      }),
    ),
  );
}
